import * as fromStandardEntity from '@app/shared/store/standard-entity.state';
import { Role } from '@cco/model';
import * as fromRole from '@core/auth/reducers/role.reducer';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State } from './auth2.reducer';

const selectAuth2State = createFeatureSelector<State>('auth2');
export const selectCurrentUser = createSelector(selectAuth2State, state => state?.currentUser);
export const selectTenantUser = createSelector(selectCurrentUser, currentUser => currentUser?.tenantUser);
export const selectCurrentUserTenantId = createSelector(
  selectCurrentUser,
  currentUser => currentUser?.tenantId,
);
export const selectCurrentUserOpaqueTenantCode = createSelector(
  selectCurrentUser,
  currentUser => currentUser?.opaqueTenantCode,
);
export const selectTenantUserName = createSelector(
  selectTenantUser,
  tenantUser => tenantUser && `${tenantUser.givenNames ?? ''} ${tenantUser.surname ?? ''}`
);
export const selectCurrentUserHasGlobalRole = createSelector(selectCurrentUser, (currentUser) => {
  if (currentUser && currentUser.roles) {
    if (currentUser.roles.findIndex(role => role.name.includes('_GLOBAL_')) !== -1) {
      return true;
    }
  }

  return false;
});
export const currentUserPrivileges = createSelector(selectAuth2State, (state) => {
  const privileges: string[] = [];

  if (!state || !state.currentUser || !state.currentUser.roles) {
    return null;
  }

  state.currentUser.roles.forEach((role) => {
    role.privileges.forEach((privilege) => {
      privileges.push(privilege.name);
    });
  });

  return privileges;
});

export interface UserHasPrivilegesProps {
  oneOf: string | string[];
  allOf: string | string[];
  allowAnonymous?: boolean;
}
export const userHasPrivileges = createSelector(
  currentUserPrivileges,
  (privileges: string[], props: UserHasPrivilegesProps) => {
    if (props.allowAnonymous === true) {
      return true;
    }

    props.oneOf = !props.oneOf ? [] : typeof props.oneOf === 'string' ? [props.oneOf] : props.oneOf;
    props.allOf = !props.allOf ? [] : typeof props.allOf === 'string' ? [props.allOf] : props.allOf;

    if (!privileges) {
      return false;
    }
    if (props.oneOf.length === 0 && props.allOf.length === 0) {
      return false;
    }

    let hasAtLeastOne = props.oneOf.length === 0;
    let missingAny = false;

    for (const p of props.oneOf) {
      if (privileges.includes(p)) {
        hasAtLeastOne = true;
      }
    }

    for (const p of props.allOf) {
      if (!privileges.includes(p)) {
        missingAny = true;
      }
    }

    return hasAtLeastOne && !missingAny;
  },
);
export const selectUserName = createSelector(
  selectCurrentUser,
  currentUser => `${currentUser?.givenNames} ${currentUser?.surname?.toUpperCase()}`,
);
export const selectUserPoolId = createSelector(
  selectCurrentUser,
  currentUser => currentUser?.userPoolId,
);

export const selectRoleState = createFeatureSelector<fromRole.State>('role');
export const selectRoles = createSelector(selectRoleState, fromRole.selectAll);
export const selectRoleEntities = createSelector(selectRoleState, fromRole.selectEntities);
export const selectRoleErrors = createSelector(selectRoleState, fromStandardEntity.selectErrors);
export const selectRoleSaving = createSelector(selectRoleState, fromStandardEntity.selectSaving);
export const selectRoleById = (id: number) =>
  createSelector(selectRoleEntities, (roles: Dictionary<Role>) => {
    if (roles) {
      return roles[id];
    } else {
      return null;
    }
  });
export const selectRolesLoadedAll = createSelector(
  selectRoleState,
  fromStandardEntity.selectLoadedAll,
);
export const loadedAllMetadata = createSelector(
  selectRoleState,
  fromStandardEntity.selectLoadedAllMetadata,
);
